/**
 * SignatureDialog — capture a tenant signature via drawing on a canvas
 * or uploading an image file. Returns a PNG blob with a transparent background.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Pencil, Upload, RotateCcw, Check, Loader2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with a PNG blob of the final signature. */
  onConfirm: (pngBlob: Blob) => Promise<void> | void;
  /** True while parent is processing the confirmation (stamps PDF + uploads). */
  submitting?: boolean;
  language?: 'en' | 'de';
};

const CANVAS_W = 560;
const CANVAS_H = 200;

export function SignatureDialog({ open, onOpenChange, onConfirm, submitting, language = 'en' }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tab, setTab] = useState<'draw' | 'upload'>('draw');
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [uploadedDataUrl, setUploadedDataUrl] = useState<string | null>(null);

  const t = (de: string, en: string) => (language === 'de' ? de : en);

  // ── Canvas setup ──
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // High DPI support
    const ratio = window.devicePixelRatio || 1;
    canvas.width = CANVAS_W * ratio;
    canvas.height = CANVAS_H * ratio;
    canvas.style.width = `${CANVAS_W}px`;
    canvas.style.height = `${CANVAS_H}px`;
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.5;
    // Transparent background so stamp blends onto PDF
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
  }, []);

  useEffect(() => {
    if (open && tab === 'draw') {
      // Defer to next frame so canvas is mounted
      requestAnimationFrame(setupCanvas);
    }
  }, [open, tab, setupCanvas]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setHasDrawn(false);
      setUploadedDataUrl(null);
      setTab('draw');
    }
  }, [open]);

  // ── Drawing handlers ──
  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const touch = 'touches' in e ? e.touches[0] : null;
    const clientX = touch ? touch.clientX : (e as React.MouseEvent).clientX;
    const clientY = touch ? touch.clientY : (e as React.MouseEvent).clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasDrawn) setHasDrawn(true);
  };

  const endDraw = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  // ── Upload handlers ──
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('Nur Bilddateien erlaubt (PNG, JPG).', 'Only image files allowed (PNG, JPG).'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('Datei ist größer als 5 MB.', 'File is larger than 5 MB.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setUploadedDataUrl(String(reader.result));
    reader.readAsDataURL(file);
  };

  // ── Confirm ──
  const handleConfirm = async () => {
    try {
      let blob: Blob | null = null;

      if (tab === 'draw') {
        if (!hasDrawn) {
          toast.error(t('Bitte zuerst unterschreiben.', 'Please draw your signature first.'));
          return;
        }
        const canvas = canvasRef.current;
        if (!canvas) return;
        blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob((b) => resolve(b), 'image/png');
        });
      } else {
        if (!uploadedDataUrl) {
          toast.error(t('Bitte Bild hochladen.', 'Please upload an image.'));
          return;
        }
        // Convert data URL → blob (preserve as PNG for transparency; repaint onto canvas to normalize)
        blob = await dataUrlToPngBlob(uploadedDataUrl);
      }

      if (!blob) {
        toast.error(t('Signatur konnte nicht gespeichert werden.', 'Could not capture signature.'));
        return;
      }

      await onConfirm(blob);
    } catch (err: any) {
      toast.error(err?.message || t('Fehler beim Speichern der Signatur.', 'Error saving signature.'));
    }
  };

  const canConfirm = tab === 'draw' ? hasDrawn : !!uploadedDataUrl;

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('Vertrag unterschreiben', 'Sign your contract')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'Zeichne deine Unterschrift oder lade ein Bild hoch. Sie wird auf den Vertrag gestempelt.',
              'Draw your signature or upload an image. It will be stamped onto your contract.',
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'draw' | 'upload')} className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="draw" disabled={submitting}>
              <Pencil className="w-4 h-4 mr-2" />
              {t('Zeichnen', 'Draw')}
            </TabsTrigger>
            <TabsTrigger value="upload" disabled={submitting}>
              <Upload className="w-4 h-4 mr-2" />
              {t('Hochladen', 'Upload')}
            </TabsTrigger>
          </TabsList>

          {/* ── Draw ── */}
          <TabsContent value="draw" className="mt-4 space-y-3">
            <div
              className="rounded-lg border-2 border-dashed bg-muted/20 p-2 flex items-center justify-center overflow-hidden"
              style={{ minHeight: CANVAS_H + 16 }}
            >
              <canvas
                ref={canvasRef}
                className="rounded-md bg-white border cursor-crosshair touch-none select-none"
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{t('Zeichne mit Maus oder Finger.', 'Draw with mouse or finger.')}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearCanvas}
                disabled={!hasDrawn || submitting}
                className="h-7"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                {t('Löschen', 'Clear')}
              </Button>
            </div>
          </TabsContent>

          {/* ── Upload ── */}
          <TabsContent value="upload" className="mt-4 space-y-3">
            {uploadedDataUrl ? (
              <div className="rounded-lg border-2 border-dashed bg-muted/20 p-4 flex flex-col items-center gap-3">
                <img
                  src={uploadedDataUrl}
                  alt="Signature preview"
                  className="max-h-48 rounded-md bg-white border shadow-sm"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setUploadedDataUrl(null)}
                  disabled={submitting}
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  {t('Anderes Bild auswählen', 'Choose different image')}
                </Button>
              </div>
            ) : (
              <label
                className="rounded-lg border-2 border-dashed bg-muted/20 p-8 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-muted/40 transition-colors"
                style={{ minHeight: CANVAS_H + 16 }}
              >
                <ImageIcon className="w-10 h-10 text-muted-foreground/60" />
                <p className="text-sm font-medium">
                  {t('Bild der Unterschrift hochladen', 'Upload signature image')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('PNG oder JPG, bis 5 MB', 'PNG or JPG, up to 5 MB')}
                </p>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  className="hidden"
                  onChange={onFileChange}
                  disabled={submitting}
                />
              </label>
            )}
            <p className="text-[11px] text-muted-foreground">
              {t(
                'Tipp: Unterschreibe auf weißem Papier, fotografiere oder scanne es und lade es hier hoch.',
                'Tip: Sign on white paper, photograph or scan it, and upload here.',
              )}
            </p>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('Abbrechen', 'Cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm || submitting}>
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            {submitting
              ? t('Wird unterschrieben...', 'Signing...')
              : t('Unterschreiben & einreichen', 'Sign & submit')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Convert a data URL to a PNG Blob by drawing to an off-screen canvas. */
async function dataUrlToPngBlob(dataUrl: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const off = document.createElement('canvas');
      off.width = img.naturalWidth;
      off.height = img.naturalHeight;
      const ctx = off.getContext('2d');
      if (!ctx) return resolve(null);
      ctx.drawImage(img, 0, 0);
      off.toBlob((b) => resolve(b), 'image/png');
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

export default SignatureDialog;
